import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Bullets, Buttons, FlexContainer, Inputs, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { hideModalAction } from 'Store/modules/app/actions';
import { useTranslation } from 'Hooks/useTranslation';
import useValidateClabe, { getErrorContext, hasError } from 'Hooks/useValidateClabe';
import { usePatchFilingUser } from 'Containers/Filing/hooks';
import { CloseIcon } from 'Components/icons';
import ClabeModalIcon from 'Components/icons/CableModalIcon';
import { removeOverflow } from 'Styles/utils/utils';
import { CLABE_HELP_URL } from 'Constants/helpsUrl';
import { openLinkInNewTab } from 'Utils/links';
import * as Styled from './styles';
import { ClabeModalProps } from './types';

const ClabeForm = ({ data }: ClabeModalProps): JSX.Element => {
  const [clabeAccount, setClabeAccount] = useState('');
  const { isValid, error, bankTag, isLoading } = useValidateClabe(clabeAccount);
  const { t } = useTranslation(locizeNS.MODALS);
  const dispatch = useDispatch();
  const closeModal = (): void => {
    removeOverflow();
    dispatch(hideModalAction());
    data?.refetch();
  };
  const { mutate, isSuccess } = usePatchFilingUser();

  useEffect(() => {
    if (data.clabe) {
      setClabeAccount(data.clabe);
    }
  }, [data]);

  useEffect(() => {
    if (isSuccess) {
      closeModal();
    }
  }, [isSuccess]);

  const handleSubmit = (): void => {
    mutate({ CLABE: clabeAccount });
  };
  const handleChange = (val): void => {
    setClabeAccount(val.replace(/\s+/g, ''));
  };

  return (
    <Styled.ModalContainer>
      <Styled.ModalContent>
        <Styled.CloseButton onClick={closeModal}>
          <CloseIcon />
        </Styled.CloseButton>
        <Styled.Container direction="column" gap="dimension_08">
          <FlexContainer justify="center">
            <ClabeModalIcon />
          </FlexContainer>
          <Text color="dark_500" type="fds_text_bold_M" align="center">
            {t('ClabeModal.Title')}
          </Text>
          <Text color="green_800" type="fds_text_medium_S" align="center">
            {t('ClabeModal.Subtitle')}
          </Text>
          <Inputs.Text
            label={t('ClabeModal.Clabe.label')}
            value={clabeAccount}
            hasError={!isLoading && hasError(clabeAccount, isValid)}
            onChange={handleChange}
            placeholder={t('filing:Filing.clabe.placeholder')}
            errorMsg={t(`filing:Filing.clabe.${getErrorContext(clabeAccount, error)}`)}
            helpButtonUrl={CLABE_HELP_URL}
            onHelpButton={url => openLinkInNewTab(url)}
          />
          {clabeAccount && isValid && (
            <Bullets
              bullets={[
                {
                  text: t('filing:Filing.clabe.tag', {
                    info: bankTag,
                  }),
                },
              ]}
              icon={{
                type: 'InfoXS',
              }}
              spacing="S"
            />
          )}
          <FlexContainer justify="center">
            <Buttons.Classic
              disabled={!isValid || isLoading}
              label={t('ClabeModal.CTA')}
              mode="primary"
              size="m"
              onClick={handleSubmit}
            />
          </FlexContainer>
        </Styled.Container>
      </Styled.ModalContent>
    </Styled.ModalContainer>
  );
};

export default ClabeForm;
